import Transitions from "@/components/@extended/Transitions";
import MainCard from "@/components/Card/MainCard";
import Logout from "@/assets/icon/Logout";
import ManageAccounts from "@/assets/icon/ManageAccounts";
import {
  Avatar,
  Box,
  ButtonBase,
  CardContent,
  ClickAwayListener,
  Divider,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  selectUser,
  setUserProfile,
} from "@/redux/features/User/userSlice";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { useEffect, useRef, useState } from "react";
import profileItems from "@/containers/menu-items/Profile";
import Label from "@/components/Label/Label";
import { useNavigate } from "react-router-dom";
import { removeAllLocalStore, removeAllSession } from "@/utils/Session";
import MoreVert from "@/assets/icon/MoreVert";
import { useLogoutMutation } from "@/redux/features/Auth/authApi";

const Profile = () => {
  const theme = useTheme();

  const matchesXs = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  const user = useAppSelector(selectUser);

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const [logout, { isSuccess: isLogoutSuccess, isError: isLogoutError }] =
    useLogoutMutation();

  const anchorEl = useRef<null | HTMLElement>(null);

  const [open, setOpen] = useState(false);

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    anchorEl.current = event.currentTarget;
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = () => {
    anchorEl.current = null;
    setOpen(false);
  };

  const handleLogout = async () => {
    logout();
  };

  useEffect(() => {
    if (isLogoutSuccess || isLogoutError) {
      removeAllSession();
      removeAllLocalStore();
      dispatch(setUserProfile(null));
      navigate("/login", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogoutSuccess, isLogoutError]);

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      {!matchesXs ? (
        <ButtonBase onClick={handleToggle}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ p: 0.5 }}
          >
            <Avatar alt="profile user" sx={{ width: 32, height: 32 }}>
              <ManageAccounts color="secondary" />
            </Avatar>
            <Typography variant="subtitle1">{user.profile?.name}</Typography>
          </Stack>
        </ButtonBase>
      ) : (
        <Box sx={{ flexShrink: 0, ml: 0.75 }}>
          <IconButton
            disableRipple
            color="secondary"
            sx={{ bgcolor: "secondary.background.paper" }}
            size="medium"
            onClick={handleToggle}
          >
            <MoreVert />
          </IconButton>
        </Box>
      )}

      <Popper
        open={open}
        anchorEl={anchorEl?.current}
        placement="bottom-end"
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 9],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="fade" in={open} {...TransitionProps}>
            <Paper
              sx={{
                boxShadow: theme.customShadows.z1,
                width: 290,
                minWidth: 240,
                maxWidth: 290,
                [theme.breakpoints.down("md")]: {
                  maxWidth: 250,
                },
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false} isContent={false}>
                  <CardContent sx={{ px: 2.5, pt: 3 }}>
                    <Grid
                      container
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Grid item>
                        <Stack
                          direction="row"
                          spacing={1.25}
                          alignItems="center"
                        >
                          <Stack>
                            <Typography variant="h5">
                              {user.profile?.name}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Grid>
                      <Grid item>
                        <IconButton
                          size="large"
                          color="primary"
                          onClick={handleLogout}
                        >
                          <Logout />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </CardContent>
                  <Divider />
                  <List component="nav">
                    {profileItems.map((item, index) => {
                      return (
                        <Box key={item.id}>
                          <ListItemButton
                            key={item.id}
                            onClick={(event) => {
                              navigate(
                                `/user/${item.url!!}`
                              );
                            }}
                          >
                            {item.icon && (
                              <ListItemIcon>{item.icon}</ListItemIcon>
                            )}

                            <ListItemText
                              primary={
                                <Label id={`${item.title}`} color="primary" />
                              }
                            />
                          </ListItemButton>
                          {profileItems.length - 1 !== index && <Divider />}
                        </Box>
                      );
                    })}
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
};

export default Profile;
